import { OpportunityParticipant } from "@entities";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { type HyperTableFilter } from "@shared/frontend/hyper-table";
import { PARTICIPANT_STATUS, PARTICIPANT_STATUS_HEB } from "@shared/helpers/constants";
import dayjs from "dayjs";

const toValidDate = (value: unknown): Date | null => {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value as string | number);
  return Number.isFinite(date.getTime()) ? date : null;
};

const filters: HyperTableFilter<OpportunityParticipant>[] = [
  { fieldName: "association.user.name", id: "name" },
  { fieldName: "association.user.phoneNumber", id: "phoneNumber" },
  { fieldName: "association.user.gender", id: "gender", caption: "מגדר" },
  {
    id: "status",
    icon: "literal",
    caption: "סטטוס",
    renderFilter: ({ onSave, meta }) => {
      const value = (meta.value as string) ?? "";
      return (
        <Box sx={{ p: 1, minWidth: 180 }}>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={value}
            onChange={(event) => {
              const next = event.target.value as (typeof PARTICIPANT_STATUS)[number] | "";
              onSave({
                value: next
                  ? OpportunityParticipant.hyperFilter({ status: next })
                  : {},
                meta: {
                  value: next,
                  text: next ? PARTICIPANT_STATUS_HEB[next] : "",
                },
              });
            }}
          >
            <MenuItem value="">הכל</MenuItem>
            {PARTICIPANT_STATUS.map((status) => (
              <MenuItem key={status} value={status}>
                {PARTICIPANT_STATUS_HEB[status]}
              </MenuItem>
            ))}
          </Select>
        </Box>
      );
    },
  },
  {
    id: "registeredManually",
    icon: "boolean",
    caption: "הרשמה ידנית",
    renderFilter: ({ onSave, meta }) => {
      const value = (meta.value as string) ?? "";
      return (
        <Box sx={{ p: 1, minWidth: 180 }}>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={value}
            onChange={(event) => {
              const next = event.target.value;
              onSave({
                value:
                  next === "yes"
                    ? OpportunityParticipant.hyperFilter({ registeredManually: true })
                    : next === "no"
                      ? OpportunityParticipant.hyperFilter({ registeredManually: false })
                      : {},
                meta: {
                  value: next,
                  text: next === "yes" ? "ידנית" : next === "no" ? "לא ידנית" : "",
                },
              });
            }}
          >
            <MenuItem value="">הכל</MenuItem>
            <MenuItem value="yes">הרשמה ידנית</MenuItem>
            <MenuItem value="no">הרשמה עצמית</MenuItem>
          </Select>
        </Box>
      );
    },
  },
  {
    id: "createdAt",
    icon: "date",
    caption: "תאריך הרשמה",
    renderFilter: ({ onSave, meta }) => {
      const startDate = toValidDate(meta.startDate);
      const endDate = toValidDate(meta.endDate);
      const buildFilter = (start: Date | null, end: Date | null) => {
        if (!start && !end) return {};
        return OpportunityParticipant.hyperFilter({
          createdAt: {
            ...(start ? { $gte: dayjs(start).startOf("day").toDate() } : {}),
            ...(end ? { $lte: dayjs(end).endOf("day").toDate() } : {}),
          },
        });
      };
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          localeText={heIL.components.MuiLocalizationProvider.defaultProps.localeText}
          adapterLocale="he"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <DatePicker
                value={startDate ? dayjs(startDate) : null}
                onChange={(date) => {
                  const next = date ? dayjs(date).toDate() : null;
                  onSave({
                    value: buildFilter(next, endDate),
                    meta: { startDate: next, endDate },
                  });
                }}
                label="מתאריך"
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                value={endDate ? dayjs(endDate) : null}
                onChange={(date) => {
                  const next = date ? dayjs(date).toDate() : null;
                  onSave({
                    value: buildFilter(startDate, next),
                    meta: { startDate, endDate: next },
                  });
                }}
                label="עד תאריך"
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              sx={{ alignSelf: "flex-start" }}
              onClick={() =>
                onSave({ value: {}, meta: { startDate: null, endDate: null } })
              }
            >
              נקה סינון
            </Button>
          </Box>
        </LocalizationProvider>
      );
    },
  },
];

export default filters;
